import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import * as FileSaver from 'file-saver';
import * as moment from 'moment';
import { MessageService, TreeNode } from 'primeng/api';
import { HandleError, HttpErrorHandler } from 'src/app/http-error-handler.service';
import { Invoice } from 'src/app/modelo/Invoice';
import { InvoiceDetail } from 'src/app/modelo/InvoiceDetail';
import { Product } from 'src/app/modelo/Product';
import { VeronicaAPIErrorResultData, VeronicaApiSubErrorData } from 'src/app/modelo/VeronicaAPIErrorData';
import { isEmptyOrNull, validateDni, validateRUC } from 'src/app/shared/helpers';
import { environment } from 'src/environments/environment';
import { ApiService, FAZILService } from '.';

export interface ResultData {
    header: string;
    body: string;
    type: string;
    subErrors: VeronicaApiSubErrorData;
}
export interface ResultsData {
    header: string;
    body: string;
    type: string;
    subErrors: VeronicaApiSubErrorData[];
}

@Injectable()
export class UIService {

    private apiServer = '';

    private handleError: HandleError;

    //Variables de configuración/mensajes
    appConfig = environment.settings;

    errors: Object = {};

    //Variables auxiliares
    nodes: TreeNode[] = [];

    constructor(
        private apiService: ApiService,
        private httpClient: HttpClient,
        private httpErrorHandler: HttpErrorHandler,
        private sanitizer: DomSanitizer,
        private messageService: MessageService,
        private fazilService: FAZILService,
    ) {
        this.apiServer = environment.settings.apiServer;
        this.handleError = httpErrorHandler.createHandleError('UIService');
    }

    /**
    *Manejo de mensajes
    */
    limpiarMessages = () => {
        this.messageService.clear();
    }

    mostrarMensaje(type: string, mensaje: string) {
        let err_summary = (type == this.appConfig.ux.messagesType.success) ? this.appConfig.ux.messages.success
            : (type == this.appConfig.ux.messagesType.warn) ? this.appConfig.ux.messages.warn
                : (type == this.appConfig.ux.messagesType.info) ? this.appConfig.ux.messages.info
                    : this.appConfig.ux.messages.error;
        let err_detail = mensaje;
        this.messageService.add({
            severity: type,
            summary: err_summary,
            detail: err_detail,
        });
    }

    mostrarError(err: any) {
        let err_summary = this.appConfig.ux.messages.error;
        let err_detail = this.appConfig.errorMsgs.errorGeneral;
        if (!err) {
            return;
        }
        if (err["error"] && err["error"]["message"]) {
            //Respuesta de error del backend (ExceptionResponse): mostrar el mensaje real
            this.errors = err["error"];
            err_detail = this.errors["message"];
        } else if (err["errors"]) {
            this.errors = err["errors"];
            err_summary = this.errors['type'];
            err_detail = `${this.errors["message"]} ${this.appConfig.errorMsgs.errorPermisos}.`
        } else {
            this.errors = err;
            err_summary = this.errors['type'] ? this.errors['type'] : err_summary;
            err_detail = this.appConfig.errorMsgs.errorDesconocido;
        }
        this.messageService.add({
            severity: this.appConfig.ux.messagesType.error,
            summary: err_summary,
            detail: err_detail,
        });
    }

    buildResultFromData(msg: string, type: string) {
        let header = "";
        let body = "";
        if (msg && msg.length > 0) {
            let msg1 = msg.split('. ');
            header = msg1[0];
            for (let i = 1; i < msg1.length; i++) {
                body = body.concat(msg1[i]).concat(".\n\n");
            }
        }
        if (!header || header.length == 0) {
            header = (type == this.appConfig.ux.messagesType.success) ? this.appConfig.ux.messages.success
                : (type == this.appConfig.ux.messagesType.warn) ? this.appConfig.ux.messages.warn
                    : (type == this.appConfig.ux.messagesType.info) ? this.appConfig.ux.messages.info
                        : this.appConfig.ux.messages.error;
        }
        if (!body || body.length == 0) {
            body = (type == this.appConfig.ux.messagesType.success
                || type == this.appConfig.ux.messagesType.info) ? 'Transacción procesada..'
                : this.appConfig.errorMsgs.errorGeneral;
        }
        return { header: header, body: body, type: type, subErrors: null }
    }

    buildResultFromVerify(msg: string, type: string) {
        return { header: this.appConfig.ux.verifingMsg, body: msg, type: type, subErrors: null }
    }

    ejecutarAccionResultFromData(event, accion: string) {
        if (accion == this.appConfig.ux.actionsExecute.subscribe) {
            this.fazilService.irALanding();
        } else if (accion == this.appConfig.ux.actionsExecute.invoicer) {
            this.fazilService.irAFactura();
        } else if (accion == this.appConfig.ux.actionsExecute.login) {
            this.fazilService.irALogin();
        } else if (accion == 'pos') {
            this.fazilService.irAInicioModulo('pos');
        } else if (accion == 'pedido') {
            this.fazilService.irAProforma('resto');
        } else if (accion == 'suscripciones'){
            this.fazilService.irASuscripciones();
        }
    }

    /**
    *Manejo de árbol y nodos
    */
    expandMinTree(tree: TreeNode[], expanded: boolean) {
        if (tree && tree.length) {
            tree.forEach((node) => {
                this.expandTreeRecursive(node, expanded);
            });
        }
    }

    expandTreeRecursive(node: TreeNode, expanded: boolean) {
        node.expanded = expanded;
        if (node.children) {
            node.children.forEach((childNode) => {
                this.expandTreeRecursive(childNode, expanded);
            });
        }
    }

    addNodesToArray(node: TreeNode, fieldKey: string) {
        this.nodes = [];
        this.addNodeToArray(node, fieldKey);
        return this.nodes;
    }

    addNodeToArray(node: TreeNode, fieldKey: string) {
        if (!node) return;
        if (node.children) {
            for (let child of node.children) {
                this.nodes.push(child.data);
                this.addNodeToArray(child, fieldKey);
            }
        }
        if (!this.nodes.find((item) => item[fieldKey] == node['data'][fieldKey])) {
            this.nodes.push(node.data);
        }
    }

    /**
    *Conversión de fotos a imagen base64
    */
    sanitize(image: any) {
        if (image) {
            if (typeof (image) == 'string' && image.includes('data:image/png')) {
                return image;
            } else {
                let imgBase64 = this.sanitizer.bypassSecurityTrustResourceUrl(image);
                return imgBase64['SafeResourceUrlImpl'] ? imgBase64['SafeResourceUrlImpl'] : imgBase64;
            }
            //return this.sanitizer.bypassSecurityTrustResourceUrl(image);
        }
        return null;
    }

    /**
    *Generación de fechas
    */
    getDateNow() {
        return new Date();
    }

    getDateMin(timeType: string) {
        let today = new Date();
        let month = today.getMonth();
        let year = today.getFullYear();
        let minDate = new Date();

        if (timeType == 'month') {
            let prevMonth = (month === 0) ? 11 : month - 1;
            let prevYear = (prevMonth === 11) ? year - 1 : year;
            minDate.setMonth(prevMonth);
            minDate.setFullYear(prevYear);
        }
        return minDate;
    }

    getDateMaxByPlan(initDate: Date, timeType: string) {
        let today = new Date(initDate)
        let month = today.getMonth();
        let year = today.getFullYear();
        let nextMonth: any;
        let nextYear: any;
        let date = new Date(today);
        switch (timeType) {
            case this.appConfig.properties.tiposPeriodos.mensual:
                nextMonth = (month === 11) ? 0 : month + 1;
                nextYear = (nextMonth === 0) ? year + 1 : year;
                date.setMonth(nextMonth);
                date.setFullYear(nextYear);
                break
            case this.appConfig.properties.tiposPeriodos.trimestral:
                nextMonth = (month === 9) ? 0 : (month === 10) ? 1 : (month === 11) ? 2 : month + 3;
                nextYear = (month === 9 || month === 10 || month === 11) ? year + 1 : year;
                date.setMonth(nextMonth);
                date.setFullYear(nextYear);
                break
            case this.appConfig.properties.tiposPeriodos.semestral:
                nextMonth = (month === 6) ? 0 : (month === 7) ? 1 : (month === 8) ? 2 : (month === 9) ? 3
                    : (month === 10) ? 4 : (month === 11) ? 5 : month + 6;
                nextYear = (month === 6 || month === 7 || month === 8 || month === 9 || month === 10 || month === 11) ? year + 1 : year;
                date.setMonth(nextMonth);
                date.setFullYear(nextYear);
                break
            case this.appConfig.properties.tiposPeriodos.anual:
                nextMonth = month;
                nextYear = year + 1;
                date.setMonth(nextMonth);
                date.setFullYear(nextYear);
                break
            case this.appConfig.properties.tiposPeriodos.bianual:
                nextMonth = month;
                nextYear = year + 2;
                date.setMonth(nextMonth);
                date.setFullYear(nextYear);
                break
            default:
                break
        }
        return date;
    }

    /**
    *Conversión de fechas
    */
    getDifferenceInDays(date1, date2) {
        const diffInMs = Math.abs(date2 - date1);
        return diffInMs / (1000 * 60 * 60 * 24);
    }

    /**
    *Manejo de listas
    */
    getValueByField(list: any[], id: number, field: string) {
        const element: any = list.find((item) => item.id === id);
        return element != null ? element[field] : this.appConfig.ux.undefinedString;
    }

    addItem(list: any[], newItem: any, fieldKey: string) {
        if (list && list.length) {
            let itemFind = list.find(item => item[fieldKey] == newItem[fieldKey]);
            if (itemFind) {
                list[list.indexOf(itemFind)] = newItem;
            } else {
                list.unshift(newItem);
            }
        } else {
            list.unshift(newItem);
        }
        return list;
    }

    removeItem(list: any[], newItem: any) {
        if (list && list.length) {
            //const indexOfObject = list.indexOf(newItem);
            const indexOfObject = list.findIndex(e => e.product?.id == newItem?.product?.id);
            list.splice(indexOfObject, 1);
        } else {
            list = [];
        }
        return list;
    }

    filteredListByField(list: any[], field: string, query: string) {
        let filtered: any[] = [];
        if (query && list && list.length) {
            for (let i = 0; i < list.length; i++) {
                let item = list[i];
                if (item[field] && item[field].toLowerCase().includes(query.toLowerCase())) {
                    filtered.push(item);
                }
            }
        } else {
            filtered = [...list];
        }
        return filtered;
    }

    filteredListByFields(list: any[], fields: string[], query: string) {
        let filtered: any[] = [];
        if (query && list && list.length) {
            for (let i = 0; i < list.length; i++) {
                let item = list[i];
                fields.forEach((field) => {
                    if (!filtered.includes(item) &&
                        item[field] && item[field].toLowerCase().includes(query.toLowerCase())) {
                        filtered.push(item);
                    }
                });
            }
        } else {
            filtered = [...list];
        }
        return filtered;
    }
    /**
    **Manejo de filto de autocomplete
    */
    filteredAutocomplete(list: any[], field: string, query: string) {
        let filtered: any[] = [];
        if (query && list && list.length) {
            for (let i = 0; i < list.length; i++) {
                let item = list[i];
                if (item[field] && item[field].toLowerCase().indexOf(query.toLowerCase()) >= 0) {
                    filtered.push(item);
                }
            }
        } else {
            filtered = [...list];
        }
        return filtered;
    }

    filteredAutocompleteByFields(list: any[], fields: string[], query: string) {
        let filtered: any[] = [];
        if (query && list && list.length) {
            for (let i = 0; i < list.length; i++) {
                let item = list[i];
                fields.forEach((field) => {
                    if (!filtered.includes(item) &&
                        item[field] && item[field].toLowerCase().indexOf(query.toLowerCase()) >= 0) {
                        filtered.push(item);
                    }
                });
            }
        } else {
            filtered = [...list];
        }
        return filtered;
    }

    /**
    **OnKey para limpiar mensajes
    */
    onKey(event: KeyboardEvent) {
        this.limpiarMessages();
    }

    /**
    *Personalizados
    */
    updateDateFormatInvoice(list: Invoice[]): Invoice[] {
        if (list && list.length) {
            list.forEach((element) => {
                if (element.authorizationDate){
                    if (this.getDifferenceInDays(new Date(element.authorizationDate), new Date()) < 16) {
                        element.fechaEmision = moment(element.authorizationDate.toString()).fromNow();
                    } else {
                        element.fechaEmision = moment(element.authorizationDate.toString()).calendar();
                    }
                } else {
                    if (this.getDifferenceInDays(new Date(element.emissionOn), new Date()) < 16) {
                        element.fechaEmision = moment(element.emissionOn.toString()).fromNow();
                    } else {
                        element.fechaEmision = moment(element.emissionOn.toString()).calendar();
                    }
                }
            });
        }
        return list;
    }

    getProductsByDetails(details: InvoiceDetail[], products: Product[]) {
        let result: Product[] = [];
        if (details && details.length) {
            let idProductsInDetails: number[] = details.map(function(obj) { return obj.product.id });
            result = products.filter((val) => idProductsInDetails.includes(val.id));
        }
        return result;
    }

    updateProductsByDetails(details: InvoiceDetail[], products: Product[]) {
        if (details && details.length) {
            details.forEach((val) => {
                if (products.find((item) => item.id == val.product.id)) {
                    products[products.indexOf(products.find(item => item.id == val.product.id))]['price'] = val.product.price; //Asegurar precio unitario original si se cambia las cantidades
                    products[products.indexOf(products.find(item => item.id == val.product.id))]['amount'] = val.amount + 1;
                }
            });
        }
        return products;
    }

    addDetailToInvoice(details: InvoiceDetail[], newDetail: InvoiceDetail, esProforma:boolean) {
        if (newDetail.amount > 0) {
            details = this.addItemDetail(details, newDetail, esProforma);
        } else {
            details = this.removeItem(details, newDetail);
        }
        return details;
    }

    addItemDetail(details: InvoiceDetail[], newDetail: InvoiceDetail, esProforma:boolean) {
        if (details && details.length) {
            let d = details.find(item => item.product.id == newDetail.product.id);
            if (d) {
                if (esProforma){
                    newDetail.amount = newDetail.amount + d.amount; //Se agrega a los que ya estaban agregados
                } else {
                    newDetail.amount = newDetail.amount; //Se reemplaza simplemente
                }
                details[details.indexOf(d)] = newDetail;
            } else {
                details.unshift(newDetail);
            }
        } else {
            details.unshift(newDetail);
        }
        return details;
    }

    checkInvoiceSimpleComplex(details: InvoiceDetail[]) {
        if (!details || details.length == 0) {
            return true;
        } else if (details.length == 1 && details[0].amount == 1) {
            return true;
        } else {
            return false;
        }
    }

    updateImageToList(list: any[], fieldPhoto: any, fieldImage: any) {
        if (list && list.length) {
            list.forEach((val) => {
                val[fieldImage] = fieldPhoto ? this.sanitize(val[fieldPhoto]) : null;
                if (!val[fieldImage]) {
                    val[fieldImage] = this.appConfig.ux.imgUser;
                }
            });
        }
        return list;
    }

    isCIorRUC(ciruc: string) {
        let code: string = ciruc ? ciruc.toString() : "";

        if (!code || code.length == 0) {
            return false;
        }

        let isRUC: boolean = false;
        let isCI: boolean = false;

        if (code && code.length == 10) {
            isCI = validateDni(code);
        } else if (code && code.length == 13) {
            isRUC = validateRUC(code);
        }

        return isCI || isRUC;
    }

    /**
    *Manejo de archivos
    */

    /**Genera y descargar un archivo de tipo .xlsx*/
    downloadXLSX(data: any, fileName: string) {
        import("xlsx").then(xlsx => {
            const worksheet = xlsx.utils.json_to_sheet(data);
            const workbook = { Sheets: { 'data': worksheet }, SheetNames: ['data'] };
            const excelBuffer: any = xlsx.write(workbook, { bookType: 'xlsx', type: 'array' });
            this.saveAsExcelFile(excelBuffer, fileName);
        });
    }

    downloadXLSXWithColsHidden(list: any[], fileName: string, cols: any) {
        let data: any = this.buildListObjectsByCols(list, cols.filter((val) => val.visible).map(function(obj) { return obj.field; }));
        this.downloadXLSX(data, fileName);
    }

    saveAsExcelFile(buffer: any, fileName: string): void {
        let EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
        let EXCEL_EXTENSION = '.xlsx';
        const data: Blob = new Blob([buffer], {
            type: EXCEL_TYPE
        });
        FileSaver.saveAs(data, fileName + '_export_' + new Date().toLocaleDateString() + EXCEL_EXTENSION);
    }

    buildListObjectsByCols(list: any[], cols: any[]) {
        let result: any[] = [];
        if (list && list.length > 0) {
            list.forEach((element) => {
                const filtered = Object.keys(element)
                    .filter(key => cols.includes(key))
                    .reduce((obj, key) => {
                        obj[key] = element[key];
                        return obj;
                    }, {});
                result.push(filtered);
            });
        }
        return result;
    }

    toLowerCase(valor: any) {
        let lowercase = "";
        if (typeof valor === 'string') {
            const texto = "" + valor;
            lowercase = texto.toLowerCase();
        }

        return lowercase;
    }


    /**Retorna el mensaje generado antes de la persistencia (guardar, eliminar) según la entidad.*/
    buildMsgConfirm(action: string, value: string) {
        let msg: string = "";
        if (action == this.appConfig.properties.defaultActions.execute.save) {
            msg = "¿Está seguro de actualizar el registro <strong>" + value + "</strong>?.";
        } else if (action == this.appConfig.properties.defaultActions.execute.delete) {
            msg = "¿Está seguro de eliminar a <strong>" + value + "</strong>?. No será posible recuperar la información.";
        } else if (action == this.appConfig.properties.defaultActions.execute.deleteNode) {
            msg = "¿Está seguro de eliminar a <strong>" + value + "</strong> y sus inferiores?. No será posible recuperar la información.";
        } else if (action == this.appConfig.properties.defaultActions.execute.saveAll) {
            msg = "¿Está seguro de actualizar los registros de <strong>" + value + "</strong>?. No será posible recuperar la información.";
        } else if (action == this.appConfig.properties.defaultActions.execute.deleteAll) {
            msg = "¿Está seguro de eliminar los registros de <strong>" + value + "</strong> seleccionados?. No será posible recuperar la información.";
        } else if (action == this.appConfig.properties.defaultActions.execute.copyAll) {
            msg = `<strong class="text-pink-700">¡Se copiaran los mismos valores a todos los items de la lista, expectuándo los campos de valor único!.</strong> ¿Está seguro de realizar esta acción?. No será posible recuperar la información.`;
        } else if (action == this.appConfig.properties.defaultActions.execute.upAll) {
            msg = "¿Está seguro de <strong>dar de alta</strong> los registros de <strong>" + value + "</strong> seleccionados?. No será posible recuperar la información.";
        } else if (action == this.appConfig.properties.defaultActions.execute.up) {
            msg = "¿Está seguro de dar de alta el registro <strong>" + value + "</strong>?. No será posible recuperar la información.";
        } else if (action == this.appConfig.properties.defaultActions.execute.preDownAll) {
            msg = "¿Está seguro de <strong> marcar para dar de baja </strong> los registros de <strong>" + value + "</strong> seleccionados?. No será posible recuperar la información.";
        } else if (action == this.appConfig.properties.defaultActions.execute.downAll) {
            msg = "¿Está seguro de dar de baja los registros de <strong>" + value + "</strong> seleccionados?. No será posible recuperar la información.";
        } else if (action == this.appConfig.properties.defaultActions.execute.confirmAvaluo) {
            msg = "¿Está seguro de confirmar los avalúos de <strong>" + value + "</strong> seleccionados?. No será posible recuperar la información.";
        } else if (action == this.appConfig.properties.defaultActions.execute.confirmar) {
            msg = "¿Está seguro de confirmar <strong>" + value + "</strong>?. No será posible recuperar la información.";
        } else if (action == this.appConfig.properties.defaultActions.execute.confirmRegistro) {
            msg = "Se registro correctamente activo, su código secuencia es: <strong>" + value + "</strong>.";
        } else if (action == this.appConfig.properties.defaultActions.execute.confirmRegistro) {
            msg = "Se registro correctamente activo, su código secuencia es: <strong>" + value + "</strong>.";
        } else if (action == this.appConfig.properties.defaultActions.execute.selectAll) {
            msg = "¿Está seguro de transferir activos fijos <strong>" + value + "</strong>?. No será posible revertir los cambios.";
        } else if (action == this.appConfig.properties.defaultActions.execute.update) {
            msg = "¿Está seguro de actualizar los registros de <strong>" + value + "</strong> desde EEASA?. No será posible recuperar la información.";
        } else if (action == this.appConfig.properties.defaultActions.execute.selectAllEquivalencia) {
            msg = "¿Está seguro de crear equivalencias para <strong>" + value + "</strong>?. No será posible recuperar la información.";
        }
        return msg;
    }


    /**Retorna el mensaje generado luego de la persistencia (guardar, eliminar) según la entidad.*/
    buildMsgSubscribe(action: string, entity: string, value: string, isNew: boolean) {
        let msg: string = "";
        if (action == this.appConfig.properties.defaultActions.execute.save) {
            if (isNew) {
                msg = `El registro de ${entity} `;
            } else {
                msg = `La actualización de ${entity} `;
            }
            msg = msg.concat(`${value} se realizó con éxito.`);
        } else if (action == this.appConfig.properties.defaultActions.execute.delete) {
            msg = `${value} ha sido eliminado con éxito.`;
        } else if (action == this.appConfig.properties.defaultActions.execute.deleteAll) {
            msg = `Los registros de ${entity} han sido eliminados con éxito.`;
        } else if (action == this.appConfig.properties.defaultActions.execute.copyAll) {
            msg = `Los valores de ${entity} para ${value} han sido copiados con éxito.`;
        } else if (action == this.appConfig.properties.defaultActions.execute.config) {
            msg = `La actualización de ${entity} para ${value} se realizó con éxito.`;
        } else if (action == this.appConfig.properties.defaultActions.execute.upAll) {
            msg = `Los registros de ${entity} han sido dados de alta con éxito.`;
        } else if (action == this.appConfig.properties.defaultActions.execute.preDownAll) {
            msg = `Los registros de ${entity} han sido marcados para ser dados de baja con éxito.`;
        } else if (action == this.appConfig.properties.defaultActions.execute.downAll) {
            msg = `Los registros de ${entity} han sido dados de baja con éxito.`;
        } else if (action == this.appConfig.properties.defaultActions.execute.uploadXLS) {
            msg = `${value} registros para ${entity} han sido procesados con éxito.`;
        } else if (action == this.appConfig.properties.defaultActions.execute.confirmAvaluo) {
            msg = `Los ${entity} de ${value} han sido confirmados con éxito.`;
        } else if (action == this.appConfig.properties.defaultActions.execute.confirmar) {
            msg = `${entity} de ${value} han sido confirmad@s con éxito.`;
        } else if (action == this.appConfig.properties.defaultActions.execute.confirmCodigoSISCAF) {
            msg = `Activo no cuenta con código SISCAF, ingresar primero para seleccionarlo.`;
        } else if (action == this.appConfig.properties.defaultActions.execute.errorFechaBusqueda) {
            msg = `Seleccionar fecha de búsqueda para continuar.`;
        } else if (action == this.appConfig.properties.defaultActions.execute.errorCodigoBusqueda) {
            msg = `Ingrese código de búsqueda para continuar.`;
        } else if (action == this.appConfig.properties.defaultActions.execute.saves) {
            msg = `Las ${entity} seleccionadas han sido guardados con éxito.`;
        } else if (action == this.appConfig.properties.defaultActions.execute.copiaValores) {
            msg = `Se copiaron valores con éxito.`;
        } else if (action == this.appConfig.properties.defaultActions.execute.errorCopiaValores) {
            msg = `No se copiaron valores, verifique error.`;
        } else if (action == this.appConfig.properties.defaultActions.execute.formIvalidLitsA) {
            msg = `¡Formulario número ${value} incompleto!. Asegúrese que el campo número de serie sea diferente y campos obligatorios estén  llenos, complete el formulario e intente nuevamente.`;
        } else if (action == this.appConfig.properties.defaultActions.execute.formIvalidLitsB) {
            msg = `¡Formulario número ${value} incompleto!. Asegúrese que los campos de número motor, número placa, número chasis sean diferentes y campos obligatorios estén  llenos, complete el formulario e intente nuevamente.`;
        } else if (action == this.appConfig.properties.defaultActions.execute.transferencia) {
            msg = `La transferecia de ${entity} para ${value} se realizó con éxito.`;
        }
        return msg;
    }
    
    async getPlatformVersion(){
        const ping = await this.fazilService.pingData();
        const serverVersionNumber = ping['versionNumber'];
        const currentClientVersionNumber:number = this.fazilService.getClienteVersionNumber();
        const data = {
            version: currentClientVersionNumber,
            serverVersion: serverVersionNumber,
            actualizar: (Number.isNaN(currentClientVersionNumber) || currentClientVersionNumber < serverVersionNumber)
            };
        return data;
    }
}